import { Redo2, Undo2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import type { RunResult } from "@/api/adapt";
import { headline, heroDate } from "@/lib/results";
import { dependants } from "@/model/graph";
import { fmtByDate, fmtMonth } from "@/lib/months";
import { useActiveScenario, useScenarioRun } from "@/hooks/world";
import { fmtValue, pct } from "@/lib/format";
import { useStore } from "@/model/store";
import { ChartSurfaceContext } from "./chart-utils";
import { OpenEditorButton } from "./ChartToolbar";

/** Chart height that fills the modal, leaving room for the toolbar and the start of the table. */
export function useModalChartHeight(reserve = 300, min = 360): number {
	const get = () => (typeof window === "undefined" ? 600 : Math.max(min, window.innerHeight - reserve));
	const [h, setH] = useState(get);
	useEffect(() => {
		const on = () => setH(get());
		window.addEventListener("resize", on);
		return () => window.removeEventListener("resize", on);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return h;
}

/**
 * The chart on a node page. It is live: handles and bars drag in place and commit on release,
 * with the same undo as the editor. The "Open editor" button (or Enter on the focused chart)
 * opens the full-screen editor with the table. Pass `openButton={false}` when the button is
 * placed in the chart's toolbar instead (use `OpenEditorButton` there).
 */
export function ChartPreview({
	onOpen,
	readOnly,
	openButton = true,
	children,
}: {
	onOpen: () => void;
	readOnly?: boolean;
	openButton?: boolean;
	children: React.ReactNode;
}) {
	const label = readOnly ? "Full screen" : "Open editor";
	return (
		<ChartSurfaceContext.Provider value={{ surface: "preview", readOnly: !!readOnly }}>
			<div
				role="group"
				tabIndex={0}
				aria-label={readOnly ? `Chart. ${label}: press Enter` : `Chart: drag the points to edit. ${label}: press Enter`}
				className="relative rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-card"
				onKeyDown={(e) => {
					if (e.key === "Enter" && !e.defaultPrevented) {
						e.preventDefault();
						onOpen();
					}
				}}
			>
				{openButton && (
					<div className="absolute -top-1 right-0 z-10">
						<OpenEditorButton onOpen={onOpen} readOnly={readOnly} />
					</div>
				)}
				{children}
			</div>
		</ChartSurfaceContext.Provider>
	);
}

/**
 * "Used in" readout for the editor (PRD §10.3): after an edit is committed, how the headline of
 * each node that uses this one moved, e.g. "Layoffs needed: 23% → 27% by end of 2027".
 */
function useDownstreamReadout(open: boolean, nodeId: string | undefined): React.ReactNode {
	const nodes = useStore((s) => s.world.nodes);
	const links = useStore((s) => s.world.links);
	const { scenario } = useActiveScenario();
	const sim = useScenarioRun(scenario?.id);
	const deps = useMemo(() => (nodeId && nodes[nodeId] ? dependants(nodeId, nodes, links) : []), [nodeId, nodes, links]);
	const prev = useRef<RunResult | null>(null);
	const [lines, setLines] = useState<{ id: string; text: string; delta: number }[] | null>(null);

	useEffect(() => {
		if (open) return;
		prev.current = null;
		setLines(null);
	}, [open]);

	useEffect(() => {
		if (!open || sim.pending || !sim.data) return;
		const before = prev.current;
		prev.current = sim.data;
		if (!before || before === sim.data) return;
		const after = sim.data;
		const out = deps
			.map((id) => {
				const n = nodes[id];
				const d = heroDate(n);
				const a = headline(n, before, d);
				const b = headline(n, after, d);
				if (a === null || b === null) return null;
				const f = (v: number) => (n.kind === "event" ? pct(v) : fmtValue(v, n.unit));
				const when = n.kind === "event" ? `by ${fmtByDate(d)}` : `in ${fmtMonth(d)}`;
				const text = f(a) === f(b) ? `${n.title}: ${f(b)} ${when}, no change` : `${n.title}: ${f(a)} → ${f(b)} ${when}`;
				const scale = n.kind === "event" ? 1 : Math.max(1e-9, Math.abs(a));
				return { id, text, delta: Math.abs(b - a) / scale };
			})
			.filter((x): x is { id: string; text: string; delta: number } => !!x)
			.sort((x, y) => y.delta - x.delta);
		setLines(out);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, sim.data, sim.pending]);

	if (!open || !deps.length) return null;
	if (sim.pending && prev.current) return <span className="text-muted-foreground">Working out what this changes downstream…</span>;
	if (!lines) return null;
	const shown = lines.slice(0, 3);
	return (
		<span className="truncate" title={lines.map((l) => l.text).join("\n")}>
			<span className="text-muted-foreground">Downstream: </span>
			{shown.map((l, i) => (
				<span key={l.id}>
					{i > 0 && <span className="text-muted-foreground"> · </span>}
					{l.text}
				</span>
			))}
			{lines.length > shown.length && <span className="text-muted-foreground"> · and {lines.length - shown.length} more</span>}
		</span>
	);
}

/** Full-screen, distraction-free editor for a chart and its table. */
export function ChartModal({
	open,
	onOpenChange,
	title,
	description,
	stats,
	nodeId,
	children,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	title: React.ReactNode;
	description: string;
	stats?: React.ReactNode;
	/** The node being edited, for the downstream readout. Defaults to the node in the URL. */
	nodeId?: string;
	children: React.ReactNode;
}) {
	const params = useParams();
	const readout = useDownstreamReadout(open, nodeId ?? params.nodeId);
	const undo = useStore((s) => s.undo);
	const redo = useStore((s) => s.redo);
	const canUndo = useStore((s) => s.canUndo);
	const canRedo = useStore((s) => s.canRedo);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="top-0 left-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none bg-background p-0 ring-0 sm:max-w-none data-open:zoom-in-100"
			>
				<header className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b bg-card px-6 py-3">
					<div className="min-w-0">
						<DialogTitle className="truncate text-lg font-semibold">{title}</DialogTitle>
						<DialogDescription className="sr-only">{description}</DialogDescription>
					</div>
					{stats && <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">{stats}</div>}
					<div className="ml-auto flex items-center gap-1">
						<Button variant="ghost" size="sm" onClick={() => void undo()} disabled={!canUndo} title="Undo (⌘Z)">
							<Undo2 className="size-4" /> Undo
						</Button>
						<Button variant="ghost" size="sm" onClick={() => void redo()} disabled={!canRedo} title="Redo (⇧⌘Z)">
							<Redo2 className="size-4" /> Redo
						</Button>
						<Button size="sm" className="ml-2" onClick={() => onOpenChange(false)}>
							Done
						</Button>
					</div>
				</header>
				<div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
					<ChartSurfaceContext.Provider value={{ surface: "editor", readout }}>{children}</ChartSurfaceContext.Provider>
				</div>
			</DialogContent>
		</Dialog>
	);
}
